import React, { useState, useEffect, useRef } from 'react';
import { RecordSelectorModal } from './RecordSelectorModal';

export function InlineCellEditor({ 
    row, 
    colName, 
    initialValue, 
    fks, 
    onSubmit, 
    onCancel,
    token
}) {
    const isFk = fks.some(fk => fk.ColumnName === colName);
    const isMultiHop = colName.includes('->');
    
    // For multihop, the "link" we are changing is the root FK.
    const rootFkCol = isMultiHop ? colName.split('->')[0] : null;
    const rootFkValue = isMultiHop ? row[rootFkCol] : null;

    // Modes: 'link' (changing the FK ID) or 'value' (changing the master text)
    // By default, if it's an FK or injected column, we assume 'link' mode.
    const canSwapLink = isFk || isMultiHop;
    const [editMode, setEditMode] = useState(canSwapLink ? 'link' : 'value');
    const [showSelector, setShowSelector] = useState(false);
    
    // The value we are editing depends on the mode
    // If 'value' mode, we edit the text value of the cell.
    const [editValue, setEditValue] = useState(initialValue || '');
    
    // Determine target table for link mode
    let targetTable = null;
    if (canSwapLink) {
        const rootCol = isMultiHop ? rootFkCol : colName;
        const fkDef = fks.find(fk => fk.ColumnName === rootCol);
        if (fkDef) targetTable = fkDef.RefTableName;
    }
    
    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [editMode]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (editMode === 'link' && isMultiHop) {
                // We are saving the root FK
                onSubmit(rootFkCol, editValue);
            } else {
                onSubmit(colName, editValue);
            }
        }
        if (e.key === 'Escape') onCancel();
    };

    const renderInput = () => {
        if (editMode === 'link') {
            return (
                <button 
                    onClick={() => setShowSelector(true)}
                    className="w-full bg-teal-900/50 border border-teal-500 text-teal-200 px-3 py-1 rounded text-sm hover:bg-teal-800 transition-colors flex items-center justify-between"
                >
                    <span className="truncate flex-1 text-left">
                        {editValue || <span className="italic text-teal-500">Select {targetTable}...</span>}
                    </span>
                    <span className="material-symbols-outlined text-[18px]">search</span>
                </button>
            );
        } else {
            return (
                <input 
                    ref={inputRef}
                    type="text" 
                    value={editValue} 
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full bg-slate-900 border border-teal-500 text-slate-200 px-2 py-1 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
            );
        }
    };

    return (
        <div className="flex flex-col gap-1 w-full min-w-[200px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2">
                {renderInput()}
                
                {canSwapLink && (
                    <button 
                        onClick={() => {
                            const newMode = editMode === 'link' ? 'value' : 'link';
                            setEditMode(newMode);
                        }}
                        className="p-1 bg-slate-800 border border-slate-600 rounded hover:bg-slate-700 transition-colors text-slate-400"
                        title={editMode === 'link' ? "Switch to edit master text value" : "Switch to select a different linked record"}
                    >
                        <span className="material-symbols-outlined text-[16px]">
                            {editMode === 'link' ? 'edit_note' : 'link'}
                        </span>
                    </button>
                )}
                
                <button onClick={() => {
                    if (editMode === 'link' && isMultiHop) onSubmit(rootFkCol, editValue);
                    else onSubmit(colName, editValue);
                }} className="p-1 text-green-400 hover:text-green-300">
                    <span className="material-symbols-outlined text-[18px]">check</span>
                </button>
                <button onClick={onCancel} className="p-1 text-red-400 hover:text-red-300">
                    <span className="material-symbols-outlined text-[18px]">close</span>
                </button>
            </div>
            
            {editMode === 'value' && canSwapLink && (
                <div className="text-[10px] text-red-400 bg-red-950/50 border border-red-900/50 px-2 py-1 rounded flex items-start gap-1 mt-1 leading-tight">
                    <span className="material-symbols-outlined text-[12px] mt-0.5">warning</span>
                    <span>Warning: You are editing the underlying master record. This will change the value for ALL records pointing to it.</span>
                </div>
            )}
            
            {showSelector && targetTable && (
                <RecordSelectorModal 
                    targetTable={targetTable}
                    token={token}
                    onCancel={() => setShowSelector(false)}
                    onSelect={(selectedRecord) => {
                        setShowSelector(false);
                        const rootCol = isMultiHop ? rootFkCol : colName;
                        const fkDef = fks.find(fk => fk.ColumnName === rootCol);
                        if (fkDef) {
                            onSubmit(rootCol, selectedRecord[fkDef.RefColumnName]);
                        }
                    }}
                />
            )}
        </div>
    );
}
